import React, { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Upload, FileText, Loader2Icon } from 'lucide-react'
import { Button } from '../../ui/button'

interface ImportAudienceModalProps {
  open: boolean
  loading: boolean
  onClose: () => void
  onImport: (csvData: FileWithData) => void
}

interface FileWithData {
  file: File
  name: string
  size: string
}

const ImportAudienceModal = ({
  open,
  loading,
  onClose,
  onImport,
}: ImportAudienceModalProps) => {
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState<FileWithData | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
  }

  const handleFileSelect = useCallback((file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setSelectedFile({
        file,
        name: file.name,
        size: formatFileSize(file.size),
      })
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        handleFileSelect(files[0])
      }
    },
    [handleFileSelect],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target
      if (files && files.length > 0) {
        handleFileSelect(files[0])
      }
    },
    [handleFileSelect],
  )

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleGenerateTable = useCallback(() => {
    if (!selectedFile) return
    onImport(selectedFile)
  }, [selectedFile])

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null)
  }, [])

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {t('chroniqueurView.uploadFile')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            {t('chroniqueurView.uploadFileDescription')}
          </p>

          {/* Drag and Drop Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragOver
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="flex flex-col items-center gap-4">
              <Upload className="h-12 w-12 text-blue-500" />
              <div>
                <p className="text-sm">
                  {t('chroniqueurView.uploadFileByClick')}{' '}
                  <button
                    type="button"
                    onClick={handleBrowseClick}
                    className="text-blue-500 hover:underline"
                  >
                    {t('chroniqueurView.browse')}
                  </button>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('chroniqueurView.maxFileSize')}
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            {t('chroniqueurView.supportedFileTypes')}
          </p>

          {/* Selected File Display */}
          {selectedFile && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{selectedFile.size}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-1 hover:bg-red-100 rounded"
              >
                <X className="h-4 w-4 text-red-500" />
              </button>
            </div>
          )}

          {/* Generate Button */}
          {selectedFile && (
            <div className="mt-6 flex justify-center">
              <Button
                onClick={handleGenerateTable}
                className="bg-gray-800 hover:bg-gray-900 text-white px-6"
                disabled={loading}
              >
                {loading && <Loader2Icon className="animate-spin" />}
                {loading
                  ? t('chroniqueurView.loading')
                  : t('chroniqueurView.generateTable')}
              </Button>
            </div>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
    </div>
  )
}

export default ImportAudienceModal
